import { FONT_CATEGORY_LABELS, FONT_FAMILIES, type FontCategory } from "@cva/shared";
import { Check, ChevronDown, Search } from "lucide-react";
import { useState } from "react";
import { useOrgFonts } from "../lib/fonts";
import { useOrgId } from "../lib/org";
import { cn } from "../lib/utils";
import { Popover, PopoverContent, PopoverTrigger } from "./ui";

/** Font family list, each name rendered in its own font. */
export function FontPicker({
  value,
  onChange,
}: {
  value: string;
  onChange: (family: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const { families } = useOrgFonts(useOrgId());
  const filtered = [...families, ...FONT_FAMILIES].filter((font) =>
    font.family.toLowerCase().includes(search.trim().toLowerCase()),
  );
  const categories = [...new Set(filtered.map((font) => font.category))] as FontCategory[];
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="flex h-8 w-full items-center justify-between gap-2 rounded-md border border-zinc-200 bg-white px-2.5 text-sm hover:bg-zinc-50"
        >
          <span className="truncate" style={{ fontFamily: `"${value}"` }}>
            {value}
          </span>
          <ChevronDown className="size-4 shrink-0 text-zinc-400" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-0">
        <div className="flex items-center gap-2 border-b border-zinc-100 px-3">
          <Search className="size-4 text-zinc-400" />
          <input
            // biome-ignore lint/a11y/noAutofocus: search field of an opened popover
            autoFocus
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Schrift suchen"
            className="h-10 w-full bg-transparent text-sm outline-none"
          />
        </div>
        <div className="max-h-80 overflow-y-auto p-1">
          {categories.map((category) => (
            <div key={category}>
              <p className="px-2 pt-2 pb-1 text-xs font-medium text-zinc-400">
                {FONT_CATEGORY_LABELS[category]}
              </p>
              {filtered
                .filter((font) => font.category === category)
                .map((font) => (
                  <button
                    key={font.family}
                    type="button"
                    onClick={() => {
                      onChange(font.family);
                      setOpen(false);
                    }}
                    className={cn(
                      "flex w-full items-center justify-between rounded-md px-2 py-1.5 text-left text-base hover:bg-zinc-100",
                      font.family === value && "bg-brand-50",
                    )}
                    style={{ fontFamily: `"${font.family}"` }}
                  >
                    {font.family}
                    {font.family === value ? <Check className="size-4 text-brand-600" /> : null}
                  </button>
                ))}
            </div>
          ))}
          {filtered.length === 0 ? (
            <p className="p-3 text-sm text-zinc-500">Keine Schrift gefunden.</p>
          ) : null}
        </div>
      </PopoverContent>
    </Popover>
  );
}
